import React, { FC } from "react";
import classnames from "classnames";

import "./index.scss";

export interface DecisionActionsProps {
  onApprove: () => void;
  onDeny: () => void;
  // Long + descriptive (e.g. "Decline recognition 7") -- the tooltip below stays the bare verb, so the
  // two never collide: this is what assistive tech reads as the button's accessible name.
  approveAriaLabel: string;
  denyAriaLabel: string;
  // Review passes neither -- redemptions' per-row in-flight state (`inFlightIds`) is the only caller
  // that needs them today.
  disabled?: boolean;
  busy?: boolean;
  className?: string;
}

/**
 * 923-core-context context/engineering/spark-rewards/phase-53-decline-reason-and-decision-buttons.md PR2 (Figma `9228:1558`, the Actions cell of frame
 * `9212:1400`): the one designed ✕/✓ decision-button pair, extracted so the Review table and the
 * Redemptions pending queue share a single implementation instead of two bespoke, drifted copies (28px
 * tinted circles on one, 32px outline circles on the other, neither matching the frame). Same
 * "extracted, not copied" discipline PillToggle's own extraction used.
 *
 * Real `<button>` elements, not the shared `Icon` (a bare `<div>`: no keyboard focus, no accessible
 * name) -- both call sites this replaces already carried that reasoning in their own comments.
 *
 * The tooltip ("Approve"/"Deny", the bare verb -- never the descriptive `aria-label` above) is CSS-only:
 * an `aria-hidden` child span revealed by `:hover`/`:focus-visible` on the button itself, so it works
 * for keyboard navigation with no React state, and never joins the button's own accessible name (which
 * assistive tech reads from `aria-label`, not from this span's text).
 *
 * @param props.onApprove - called when the ✓ button is clicked.
 * @param props.onDeny - called when the ✕ button is clicked. Never a direct decision dispatch at a
 *   call site where deny requires a reason -- the caller decides what "deny" means (a straight dispatch
 *   for a context with no reason requirement, or opening a reason overlay for one that has it).
 * @param props.approveAriaLabel - the ✓ button's full accessible name.
 * @param props.denyAriaLabel - the ✕ button's full accessible name.
 * @param props.disabled - disables both buttons (e.g. a per-row decision already in flight).
 * @param props.busy - reflected as `aria-busy` on both buttons.
 * @param props.className - merged onto the wrapping element, never replacing its base class.
 */
const DecisionActions: FC<DecisionActionsProps> = ({
  onApprove,
  onDeny,
  approveAriaLabel,
  denyAriaLabel,
  disabled,
  busy,
  className,
}) => (
  <div className={classnames("decision-actions", className)}>
    <button
      type="button"
      className="decision-action decision-action--deny"
      aria-label={denyAriaLabel}
      disabled={disabled}
      aria-busy={busy}
      onClick={onDeny}
    >
      ×
      <span className="decision-action-tooltip" aria-hidden="true">
        Deny
      </span>
    </button>
    <button
      type="button"
      className="decision-action decision-action--approve"
      aria-label={approveAriaLabel}
      disabled={disabled}
      aria-busy={busy}
      onClick={onApprove}
    >
      ✓
      <span className="decision-action-tooltip" aria-hidden="true">
        Approve
      </span>
    </button>
  </div>
);

export default DecisionActions;
